import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Zap, Brain, Search, ArrowRight, ChevronRight, Sparkles,
  Target, BarChart2, Shield, Layers, Cpu, Globe, Star, Check,
  TrendingUp, Users, Loader2, ArrowUpRight
} from 'lucide-react';
import { aiSearchTools, fetchTools, subscribeNewsletter, getToolUrl } from '../lib/supabase';
import './Home.css';

/* ─── Data ─── */
const STATS = [
  { value: '12,000+', label: 'AI Tools indexed' },
  { value: '98%',     label: 'Match accuracy' },
  { value: '4.2min',  label: 'Avg. discovery time' },
  { value: '50,000+', label: 'Builders using ToolMind' },
];

const FEATURES = [
  {
    icon: Brain,
    color: '#6d4fff',
    glow: 'rgba(109,79,255,0.3)',
    title: 'AI-Powered Matching',
    desc: 'Describe your workflow in plain language. Our AI understands context, budget, and team size to surface tools that actually fit.',
  },
  {
    icon: Search,
    color: '#b44dff',
    glow: 'rgba(180,77,255,0.3)',
    title: 'Semantic Search',
    desc: 'Go beyond keywords. Search by use case, industry, or problem — ToolMind maps meaning, not just words.',
  },
  {
    icon: BarChart2,
    color: '#00e5ff',
    glow: 'rgba(0,229,255,0.25)',
    title: 'Side-by-Side Compare',
    desc: 'Compare tools on features, pricing, integrations, and community trust scores in a single unified view.',
  },
  {
    icon: Target,
    color: '#f472b6',
    glow: 'rgba(244,114,182,0.25)',
    title: 'Personalized Stacks',
    desc: 'Save, organize, and share your tool stack. Get smart alerts when better alternatives emerge.',
  },
  {
    icon: Shield,
    color: '#22d3a0',
    glow: 'rgba(34,211,160,0.25)',
    title: 'Trust & Transparency',
    desc: 'Every recommendation includes real user reviews, G2/Capterra scores, and verified pricing — no dark patterns.',
  },
  {
    icon: Layers,
    color: '#fbbf24',
    glow: 'rgba(251,191,36,0.25)',
    title: 'Integration Graph',
    desc: 'See how tools connect. Map your entire tech stack and discover gaps before they become blockers.',
  },
];


const HOW_STEPS = [
  { n: '01', icon: Search,   title: 'Describe your need',    desc: 'Tell ToolMind what you\'re building, your team size, budget, and tech stack.' },
  { n: '02', icon: Cpu,      title: 'AI scans 12K+ tools',  desc: 'Our model cross-references features, pricing, and fit scores across every tool in the catalog.' },
  { n: '03', icon: Sparkles, title: 'Get ranked matches',    desc: 'Receive a curated shortlist ranked by relevance, budget fit, and community trust.' },
];

/* ─── Hero Section ─── */
function Hero() {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchResult, setSearchResult] = useState(null); // { query, reasoning, results }
  const navigate = useNavigate();

  const PLACEHOLDERS = [
    'Find me a no-code database for my SaaS...',
    'Best AI writing tool under $50/mo...',
    'Slack alternative for small teams...',
    'CRM for early-stage B2B startups...',
  ];
  const [ph, setPh] = useState(0);

  const placeholderCount = PLACEHOLDERS.length;
  useEffect(() => {
    if (searchResult) return; // stop rotating once there's a result showing
    const t = setInterval(() => setPh(p => (p + 1) % placeholderCount), 3000);
    return () => clearInterval(t);
  }, [placeholderCount, searchResult]);

  const runSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    try {
      const data = await aiSearchTools(q);
      setSearchResult(data);
    } catch (err) {
      console.error(err);
      setSearchError('Search is temporarily unavailable. Please try again in a moment.');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchResult(null);
    setSearchError(null);
    setQuery('');
  };

  return (
    <section className="hero">
      {/* Grid overlay */}
      <div className="hero__grid" aria-hidden />

      <div className="hero__inner section-inner">
        {/* Announcement pill */}
        <a href="#features" className="hero__pill animate-fade-up">
          <span className="hero__pill-dot" />
          <span>Introducing ToolMind v2 — Smarter AI matching</span>
          <ChevronRight size={14} />
        </a>

        {/* Headline */}
        <h1 className="hero__title animate-fade-up delay-1">
          Discover the <span className="grad-text">perfect tool</span><br />
          for every workflow
        </h1>

        <p className="hero__sub animate-fade-up delay-2">
          Stop wasting hours comparing tools. ToolMind's AI scans our catalog
          and surfaces exactly what your team needs — in seconds.
        </p>

        {/* Search bar */}
        <form className="hero__search glass-hi animate-fade-up delay-3" onSubmit={runSearch}>
          <Search size={20} className="hero__search-icon" />
          <input
            className="hero__search-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={PLACEHOLDERS[ph]}
            disabled={searching}
          />
          {searchResult && (
            <button type="button" className="hero__search-clear" onClick={clearSearch} aria-label="Clear search">
              ✕
            </button>
          )}
          <button type="submit" className="btn btn-primary btn-sm hero__search-btn" disabled={searching || !query.trim()}>
            {searching ? <Loader2 size={14} className="hero__spin" /> : <>Search <Zap size={14} /></>}
          </button>
        </form>

        {/* AI Search Results */}
        {searchError && (
          <div className="hero__search-error animate-fade-up">{searchError}</div>
        )}

        {searchResult && (
          <div className="hero__results glass-hi animate-fade-up">
            {searchResult.reasoning && (
              <div className="hero__results-reasoning">
                <Sparkles size={14} style={{ color: 'var(--text-accent)', flexShrink: 0, marginTop: 2 }} />
                <p>{searchResult.reasoning}</p>
              </div>
            )}
            {searchResult.results.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 'var(--sp-2) 0' }}>
                No tools matched that query. Try different keywords, or browse the full catalog.
              </p>
            ) : (
              <div className="hero__results-list">
                {searchResult.results.slice(0, 5).map(tool => (
                  <a
                    key={tool.id}
                    href={getToolUrl(tool)}
                    target="_blank"
                    rel="noreferrer"
                    className="hero__result-card"
                  >
                    <div className="hero__result-icon" style={{ background: `${tool.logo_color}18`, border: `1px solid ${tool.logo_color}30` }}>
                      <span style={{ color: tool.logo_color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 16 }}>
                        {tool.name[0]}
                      </span>
                    </div>
                    <div className="hero__result-info">
                      <div className="hero__result-name">{tool.name}</div>
                      <div className="hero__result-tagline">{tool.tagline}</div>
                    </div>
                    <div className="hero__result-meta">
                      <span className="hero__result-price">{tool.price_display}</span>
                      <ArrowUpRight size={14} style={{ color: 'var(--text-muted)' }} />
                    </div>
                  </a>
                ))}
              </div>
            )}
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--sp-4)' }} onClick={() => navigate('/tools')}>
              Browse full catalog <ArrowRight size={14} />
            </button>
          </div>
        )}

        {/* Floating cards — sit between search and CTAs, never overlap */}
        {!searchResult && (
          <div className="hero__cards animate-fade-up delay-4" aria-hidden>
            <div className="hero__card hero__card--left glass">
              <div className="hero__card-icon" style={{ background: 'rgba(109,79,255,0.2)', color: '#6d4fff' }}>
                <TrendingUp size={16} />
              </div>
              <div>
                <div className="hero__card-title">Top match found</div>
                <div className="hero__card-sub">Linear · 98% fit score</div>
              </div>
            </div>
            <div className="hero__card hero__card--right glass">
              <div className="hero__card-icon" style={{ background: 'rgba(34,211,160,0.2)', color: '#22d3a0' }}>
                <Users size={16} />
              </div>
              <div>
                <div className="hero__card-title">Trusted by builders</div>
                <div className="hero__card-sub">50,000+ teams onboard</div>
              </div>
            </div>
          </div>
        )}

        {/* CTA row */}
        {!searchResult && (
          <div className="hero__ctas animate-fade-up delay-5">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Start for free <ArrowRight size={18} />
            </Link>
            <Link to="/tools" className="btn btn-ghost btn-lg">
              Explore tools
            </Link>
          </div>
        )}

        {/* Stats */}
        {!searchResult && (
          <div className="hero__stats animate-fade-up" style={{ animationDelay: '600ms' }}>
            {STATS.map(({ value, label }) => (
              <div key={label} className="hero__stat">
                <span className="hero__stat-value grad-text">{value}</span>
                <span className="hero__stat-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── How It Works ─── */
function HowItWorks() {
  return (
    <section className="section" id="how">
      <div className="section-inner">
        <div className="how__header">
          <div className="section-label"><Cpu size={12} /> How It Works</div>
          <h2 className="section-title">
            From idea to the <span className="grad-text">right tool</span><br />in under 5 minutes
          </h2>
          <p className="section-sub">No more endless comparison tabs. Three steps is all it takes.</p>
        </div>

        <div className="how__steps">
          {HOW_STEPS.map(({ n, icon: Icon, title, desc }, i) => (
            <div key={n} className="how__step glass">
              <div className="how__step-num">{n}</div>
              <div className="how__step-icon">
                <Icon size={24} />
              </div>
              <h3 className="how__step-title">{title}</h3>
              <p className="how__step-desc">{desc}</p>
              {i < HOW_STEPS.length - 1 && (
                <div className="how__connector" aria-hidden>
                  <ArrowRight size={16} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Features ─── */
function Features() {
  return (
    <section className="section" id="features">
      <div className="section-inner">
        <div className="features__header">
          <div className="section-label"><Sparkles size={12} /> Features</div>
          <h2 className="section-title">
            Built for how <span className="grad-text">builders think</span>
          </h2>
          <p className="section-sub">
            Every feature designed around one question: how do I find the right tool faster?
          </p>
        </div>

        <div className="features__grid">
          {FEATURES.map(({ icon: Icon, color, glow, title, desc }, i) => (
            <div key={title} className={`feature-card glass ${i === 0 ? 'feature-card--wide' : ''}`}>
              <div className="feature-card__icon" style={{ background: `rgba(${hexToRgb(color)},0.12)`, boxShadow: `0 0 20px ${glow}` }}>
                <Icon size={22} style={{ color }} />
              </div>
              <h3 className="feature-card__title">{title}</h3>
              <p className="feature-card__desc">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function hexToRgb(hex) {
  if (!hex.startsWith('#')) return '109,79,255';
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

/* ─── Tools Preview ─── */
function ToolsPreview() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTools({ sort: 'trend' })
      .then(data => setTools(data.slice(0, 6)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="section" id="catalog">
      <div className="section-inner">
        <div className="tools-prev__header">
          <div>
            <div className="section-label"><Globe size={12} /> Tool Catalog</div>
            <h2 className="section-title">
              Curated tools.<br /><span className="grad-text">All in one place.</span>
            </h2>
            <p className="section-sub">Verified, categorized, and ranked by real usage data.</p>
          </div>
          <Link to="/tools" className="btn btn-ghost">
            View all tools <ArrowRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="tools-prev__grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="tool-card glass-hi" style={{ opacity: 0.4 }}>
                <div className="tool-card__icon" style={{ background: 'var(--glass-2)' }} />
                <div className="tool-card__info">
                  <div className="tool-card__name">Loading…</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tools-prev__grid">
            {tools.map(tool => (
              <a key={tool.id} href={getToolUrl(tool)} target="_blank" rel="noreferrer" className="tool-card glass-hi">
                <div className="tool-card__icon" style={{ background: `${tool.logo_color}18` }}>
                  <span style={{ color: tool.logo_color, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>
                    {tool.name[0]}
                  </span>
                </div>
                <div className="tool-card__info">
                  <div className="tool-card__name">{tool.name}</div>
                  <div className="tag">{tool.category}</div>
                </div>
                <div className="tool-card__meta">
                  <div className="tool-card__score">
                    <Star size={12} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                    <span>{tool.score}</span>
                  </div>
                  <div className="tool-card__users">
                    <Users size={12} />
                    <span>{tool.user_count_display}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Social proof ─── */
function Testimonials() {
  const QUOTES = [
    { text: 'ToolMind cut our tool evaluation time from 3 days to 20 minutes. It actually understands what we\'re building.', author: 'Sarah K.', role: 'CTO at Flare', avatar: 'S' },
    { text: 'I used to spend hours on G2 and Capterra. Now I just describe my problem and get an instant shortlist that\'s actually relevant.', author: 'Marcus T.', role: 'Founder, Buildflow', avatar: 'M' },
    { text: 'The integration graph alone is worth it. We discovered 3 tools we had duplicated in our stack — saved us $800/mo.', author: 'Priya R.', role: 'Head of Ops, Nexus', avatar: 'P' },
  ];
  return (
    <section className="section">
      <div className="section-inner">
        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-12)' }}>
          <div className="section-label" style={{ margin: '0 auto var(--sp-4)' }}><Star size={12} /> Testimonials</div>
          <h2 className="section-title">Trusted by <span className="grad-text">50,000+ builders</span></h2>
        </div>
        <div className="testimonials__grid">
          {QUOTES.map(({ text, author, role, avatar }) => (
            <div key={author} className="testimonial glass">
              <div className="testimonial__stars">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} style={{ color: '#fbbf24', fill: '#fbbf24' }} />)}
              </div>
              <p className="testimonial__text">"{text}"</p>
              <div className="testimonial__author">
                <div className="testimonial__avatar">{avatar}</div>
                <div>
                  <div className="testimonial__name">{author}</div>
                  <div className="testimonial__role">{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Newsletter ─── */
function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | duplicate | error

  const handle = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await subscribeNewsletter(email);
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus(err.message === 'already_subscribed' ? 'duplicate' : 'error');
    }
  };

  return (
    <section className="section">
      <div className="section-inner">
        <div className="newsletter glass-hi">
          <div className="newsletter__glow" aria-hidden />
          <div className="section-label" style={{ margin: '0 auto var(--sp-4)' }}>
            <Zap size={12} /> Newsletter
          </div>
          <h2 className="newsletter__title section-title">
            Stay ahead of the <span className="grad-text">AI tool curve</span>
          </h2>
          <p className="newsletter__sub section-sub" style={{ margin: '0 auto var(--sp-8)', textAlign: 'center' }}>
            Weekly roundups of new tools, AI discoveries, and stack recommendations — curated by ToolMind's AI.
          </p>

          {status === 'success' ? (
            <div className="newsletter__success">
              <div className="newsletter__success-icon">
                <Check size={20} />
              </div>
              <div>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)' }}>You're on the list!</p>
                <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Expect your first issue this Friday.</p>
              </div>
            </div>
          ) : (
            <form className="newsletter__form" onSubmit={handle}>
              <div className="newsletter__input-wrap glass">
                <input
                  type="email"
                  className="newsletter__input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={status === 'loading'}
                  required
                />
                <button type="submit" className="btn btn-primary" disabled={status === 'loading'}>
                  {status === 'loading' ? 'Subscribing…' : <><span>Subscribe</span><ArrowRight size={16} /></>}
                </button>
              </div>
              {status === 'duplicate' && (
                <p className="newsletter__note" style={{ color: 'var(--warning)' }}>
                  That email is already subscribed — you're good!
                </p>
              )}
              {status === 'error' && (
                <p className="newsletter__note" style={{ color: 'var(--error)' }}>
                  Something went wrong. Please try again.
                </p>
              )}
              {(status === 'idle' || status === 'loading') && (
                <p className="newsletter__note">
                  <Shield size={12} /> No spam. Unsubscribe anytime. We hate bad emails too.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─── CTA Banner ─── */
function CtaBanner() {
  return (
    <section className="section cta-banner__wrap">
      <div className="section-inner">
        <div className="cta-banner">
          <div className="cta-banner__glow" aria-hidden />
          <h2 className="cta-banner__title section-title">
            The right tool changes <span className="grad-text">everything.</span>
          </h2>
          <p className="section-sub" style={{ margin: '0 auto var(--sp-8)', textAlign:'center' }}>
            Join 50,000+ teams who found their perfect stack with ToolMind.
          </p>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/signup" className="btn btn-primary btn-lg">
              Get started free <ArrowRight size={18} />
            </Link>
            <Link to="/tools" className="btn btn-ghost btn-lg">Browse catalog</Link>
          </div>
          <p style={{ marginTop: 'var(--sp-4)', color: 'var(--text-muted)', fontSize: 13 }}>
            Free forever plan · No credit card required
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Home() {
  return (
    <main className="home">
      <Hero />
      <div className="divider" />
      <HowItWorks />
      <div className="divider" />
      <Features />
      <div className="divider" />
      <ToolsPreview />
      <div className="divider" />
      <Testimonials />
      <div className="divider" />
      <Newsletter />
      <div className="divider" />
      <CtaBanner />
    </main>
  );
}
